import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { AjaxListingContext, AjaxListingPagination } from '$partials/base/ajax-listing.jsx';
import { Footer, Header, Meta } from '$partials/global';
import { useAjaxListing } from '$hooks/ajax-listing';
import { ArticleStandardListingItem, ArticleFeaturedListingItem } from '$partials/listings/items';
import { useLocale } from '$contexts/locale.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

const Marker = ({ colour = '#FC5200' }) => {
    return (
        <svg
            className='inline'
            style={{ width: '8px', height: '8px' }}
            viewBox='0 0 8 8'
            xmlns='http://www.w3.org/2000/svg'>
            <circle
                cx='4'
                cy='4'
                fill={colour}
                r='4' />
        </svg>
    );
};

const FilterList = ({
    appliedFilter,
    filterOption,
    index,
    setFilter
}) => {
    let isCurrent = appliedFilter.title === filterOption.title;

    return (
        <div className='text-center'>
            <button
                className=' type-style-7'
                onClick={() => setFilter(filterOption)}
                type='button'>
                {filterOption.title}
            </button>
            {isCurrent &&
                <div className={`absolute left-0 right-0 mt-5 ${index === 0 ? 'pr-20' : ''}`}>
                    <Marker />
                </div>
            }

            {!isCurrent &&
                <div className={`absolute left-0 right-0 mt-5 invisible group-hover:visible ${index === 0 ? 'pr-20' : ''}`}>
                    <Marker colour='#00000' />
                </div>
            }
        </div>
    );
};

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ArticlesListingPage = ({ data, domain }) => {
    const page = data?.page;

    const { current: locale } = useLocale();
    const [filter, setFilter] = useState(page.topics?.find(topic => topic.slug === data?.items?.filters?.topic));

    let query = { locale: locale?.code };
    let path = '/api/v1/listings/articles';
    switch (page.__typename) {
    case 'BvaMagazineSportPage':
        query.activity = page.slug;
        query.topic = filter?.slug;
        path = '/api/v1/listings/activities';
        break;
    case 'BvaMagazineTopicPage':
        query.topic = page.slug;
        break;
    default:
        query.topic = filter?.slug;
    }

    const listing = useAjaxListing({
        items: data?.items?.items,
        page: data?.items?.page || 1,
        path: path,
        totalItems: data?.items?.total,
        totalPages: data?.items?.totalPages,
        query
    });

    return (
        <>
            <Meta page={page} />

            <main
                className='bg-grey min-h-screen'
                id='main-content'>
                <div className='layout-container'>
                    <Header
                        menu={data?.navigation}
                        search={data?.search} />
                    <article>
                        {/* Header */}
                        <header className='px-10 sm:px-20 pt-60 sm:pt-75 md:pt-100'>
                            <h1 className='type-style-2 w-full sm:w-4/6 md:w-10/12'>{page.title}</h1>
                            {page.topics?.length > 1 && (
                                <div className='type-style-7 mt-20 md:mt-44 mb-60 md:mb-60'>
                                    {page.topics.length > 1 && (
                                        <div className='flex flex-col w-screen sm:w-auto -ml-20 pl-20'>
                                            <ul className='list-none flex overflow-x-scroll pb-20'>
                                                {page.topics.map((item, idx) => (
                                                    <li
                                                        className={`group inline-block relative flex-shrink-0 ${!idx ? 'pr-20' : 'px-20'} pb-[12px]`}
                                                        key={item.slug}>
                                                        <FilterList
                                                            appliedFilter={filter}
                                                            filterOption={item}
                                                            index={idx}
                                                            setFilter={setFilter} />
                                                    </li>
                                                ))}
                                            </ul>
                                            <hr className='w-full border-b border-light-grey/20 -mt-20'/>
                                        </div>
                                    )}
                                </div>
                            )}
                        </header>

                        {/* Featured article */}
                        {page?.featuredArticle?.article &&
                        <section>
                            <ArticleFeaturedListingItem {...page.featuredArticle} />
                        </section>
                        }

                        {/* Listing */}
                        <AjaxListingContext.Provider value={listing}>
                            <section
                                aria-busy={listing.loading}
                                aria-label='Articles'
                                className='flex flex-wrap mt-40'
                                role='feed'>
                                {listing?.items.length > 0 ? (listing?.items?.map((item, i) => {
                                    if (!item) { return null; }
                                    return (
                                        <article
                                            aria-label={item.title}
                                            aria-posinset={i + 1}
                                            aria-setsize={listing.totalItems}
                                            className={'flex flex-col w-full sm:w-6/12 md:w-4/12 pb-50 md:pb-70 px-10 sm:px-20'}
                                            key={`${item.sys?.id}-${i}`}
                                            tabIndex={-1}>
                                            <ArticleStandardListingItem {...item} />
                                        </article>
                                    );
                                })) : (
                                    <div className='px-10 sm:px-20 mb-60'>No results</div>
                                )}

                                <div className='border-b border-dark-grey mx-10 sm:mx-20 w-full' />
                            </section>
                            {listing.totalPages > 1 && (
                                <div className='w-full px-10 sm:px-20 mt-30 mb-20 sm:mb-0 flex items-start'>
                                    <AjaxListingPagination
                                        current={listing.page}
                                        totalPages={listing.totalPages}/>
                                </div>
                            )}
                        </AjaxListingContext.Provider>
                    </article>

                    <Footer
                        domain={domain}
                        menu={data?.footer} />
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

Marker.propTypes = {
    colour: PropTypes.string,
};

FilterList.propTypes = {
    appliedFilter: PropTypes.object,
    filterOption: PropTypes.object,
    index: PropTypes.number,
    setFilter: PropTypes.func,
};

const propTypes = {
    data: PropTypes.shape({
        items: PropTypes.shape({
            filters: PropTypes.object,
            items: PropTypes.array,
            page: PropTypes.number,
            total: PropTypes.number,
            totalPages: PropTypes.number,
        }),
        page: PropTypes.object,
        footer: PropTypes.shape({
            items: PropTypes.array,
        }),
        navigation: PropTypes.object,
        search: PropTypes.object,
    }),
    domain: PropTypes.string,
};

ArticlesListingPage.propTypes = propTypes;

export default ArticlesListingPage;
